import { useState, useEffect, useCallback } from 'react';
import { X, Search, Plus, Trash2, Check, Loader2, AlertCircle, CheckCircle2, BarChart3 } from 'lucide-react';
import { groupsAPI, instrumentAPI } from '../../../../../services';
import { getInstrumentCategory, CATEGORY_ORDER } from '../utils';

const CAT_ICONS = {
  Forex: '\uD83D\uDCB1', Crypto: '\uD83E\uDE99', Stocks: '\uD83D\uDCC8', Commodities: '\uD83C\uDFED', Indices: '\uD83D\uDCCA',
};

export default function InstrumentPanel({ groupId, groupName, onSuccess, onClose }) {
  const [currentInstruments, setCurrentInstruments] = useState([]);
  const [availableInstruments, setAvailableInstruments] = useState([]);
  const [availableMap, setAvailableMap] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [activeTab, setActiveTab] = useState('current');
  const [selected, setSelected] = useState(new Set());
  const [editingSpreads, setEditingSpreads] = useState({});
  const [editingSwaps, setEditingSwaps] = useState({});
  // Manual add
  const [newSymbol, setNewSymbol] = useState('');
  const [newSpread, setNewSpread] = useState('');

  const showMsg = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 3000); };

  const extract = (r) => {
    if (!r) return {};
    if (r.data?.data) return r.data.data;
    if (r.data) return r.data;
    return r;
  };

  const fetchGroupInstruments = useCallback(async () => {
    try {
      setFetching(true);
      setError('');
      const result = await groupsAPI.getGroupDetails(groupId);
      const details = extract(result);
      const ai = details.allowedInstruments || [];
      const enriched = Array.isArray(ai)
        ? ai.map((item) => (typeof item === 'object' && item.symbol ? { ...item } : { symbol: item, spread_pipettes: '', swap: null, type: 'Unknown', isActive: true }))
        : [];
      setCurrentInstruments(enriched);
    } catch (err) {
      setError(err.message || 'Failed to load instruments');
    } finally {
      setFetching(false);
    }
  }, [groupId]);

  const fetchAvailable = useCallback(async () => {
    try {
      const response = await instrumentAPI.getAll();
      let all = [];
      let map = {};
      const data = response.data?.data || response.data;
      const list = Array.isArray(data) ? data : data?.instruments || [];
      list.forEach((inst) => {
        const sym = inst.symbol || inst.name || inst;
        if (sym) {
          all.push(sym);
          map[sym] = { type: inst.type || 'Other', description: inst.description || '', ...inst };
        }
      });
      const currentSyms = new Set(currentInstruments.map((i) => i.symbol));
      setAvailableInstruments(all.filter((s) => !currentSyms.has(s)));
      setAvailableMap(map);
    } catch {
      setAvailableInstruments([]);
      setAvailableMap({});
    }
  }, [currentInstruments]);

  useEffect(() => { fetchGroupInstruments(); }, [fetchGroupInstruments]);
  useEffect(() => { fetchAvailable(); }, [fetchAvailable]);

  // Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && !loading) onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [loading, onClose]);

  // Add
  const addInstrument = async (symbol, spread = '') => {
    try {
      setLoading(true);
      setError('');
      await groupsAPI.addInstrumentToGroup(groupId, symbol, spread);
      await fetchGroupInstruments();
      showMsg(`${symbol} added`);
    } catch (err) {
      setError(err.message || 'Failed to add instrument');
    } finally {
      setLoading(false);
    }
  };

  // Remove
  const removeInstrument = async (symbol) => {
    if (!window.confirm(`Remove ${symbol} from this group?`)) return;
    try {
      setLoading(true);
      setError('');
      await groupsAPI.removeInstrumentFromGroup(groupId, symbol);
      await fetchGroupInstruments();
      showMsg(`${symbol} removed`);
    } catch (err) {
      setError(err.message || 'Failed to remove instrument');
    } finally {
      setLoading(false);
    }
  };

  // Save spread
  const saveSpread = async (symbol, spreadVal) => {
    try {
      setLoading(true);
      setError('');
      const updated = currentInstruments.map((i) =>
        i.symbol === symbol ? { ...i, spread_pipettes: spreadVal } : i
      );
      await groupsAPI.updateGroup(groupId, { allowedInstruments: updated });
      setEditingSpreads((prev) => { const n = { ...prev }; delete n[symbol]; return n; });
      await fetchGroupInstruments();
      showMsg(`Spread updated for ${symbol}`);
    } catch (err) {
      setError(err.message || 'Failed to update spread');
    } finally {
      setLoading(false);
    }
  };

  // Save per-instrument swap
  const saveSwap = async (symbol, swapVal) => {
    try {
      setLoading(true);
      setError('');
      const swapNum = swapVal === '' || swapVal === null || swapVal === undefined ? null : parseFloat(swapVal);
      if (swapNum !== null && (isNaN(swapNum) || swapNum < 0 || swapNum > 0.1)) {
        setError('Swap must be between 0 and 0.1 (10%)');
        setLoading(false);
        return;
      }
      const updated = currentInstruments.map((i) =>
        i.symbol === symbol ? { ...i, swap: swapNum } : i
      );
      await groupsAPI.updateGroup(groupId, { allowedInstruments: updated });
      setEditingSwaps((prev) => { const n = { ...prev }; delete n[symbol]; return n; });
      await fetchGroupInstruments();
      showMsg(`Swap updated for ${symbol}`);
    } catch (err) {
      setError(err.message || 'Failed to update swap');
    } finally {
      setLoading(false);
    }
  };

  // Bulk
  const bulkAction = async (action) => {
    if (selected.size === 0) return;
    const syms = [...selected];
    if (!window.confirm(`${action === 'remove' ? 'Remove' : 'Add'} ${syms.length} instrument(s)?`)) return;
    try {
      setLoading(true);
      setError('');
      for (const sym of syms) {
        if (action === 'remove') await groupsAPI.removeInstrumentFromGroup(groupId, sym);
        else await groupsAPI.addInstrumentToGroup(groupId, sym);
      }
      setSelected(new Set());
      await fetchGroupInstruments();
      showMsg(`${syms.length} instrument(s) ${action === 'remove' ? 'removed' : 'added'}`);
    } catch (err) {
      setError(err.message || 'Bulk action failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (sym) => {
    setSelected((prev) => { const n = new Set(prev); if (n.has(sym)) n.delete(sym); else n.add(sym); return n; });
  };

  const filterFn = (item) => {
    const sym = typeof item === 'string' ? item : item.symbol;
    return sym.toLowerCase().includes(searchTerm.toLowerCase());
  };

  const groupItems = (items, isCurrent) => {
    const groups = {};
    CATEGORY_ORDER.forEach((c) => (groups[c] = []));
    groups.Other = [];
    items.forEach((item) => {
      const sym = isCurrent ? item.symbol : item;
      const cat = isCurrent ? (item.type || getInstrumentCategory(sym, availableMap)) : (availableMap[sym]?.type || getInstrumentCategory(sym, availableMap));
      const bucket = groups[cat] ? cat : 'Other';
      groups[bucket].push(item);
    });
    Object.keys(groups).forEach((k) => { if (groups[k].length === 0) delete groups[k]; });
    return groups;
  };

  const filteredCurrent = currentInstruments.filter(filterFn);
  const filteredAvailable = availableInstruments.filter((s) => s.toLowerCase().includes(searchTerm.toLowerCase()));

  if (fetching) {
    return (
      <div className="gm-panel-overlay">
        <div className="gm-panel-backdrop" />
        <div className="gm-panel" style={{ maxWidth: '36rem', alignItems: 'center', justifyContent: 'center' }}>
          <div className="gm-spinner" />
        </div>
      </div>
    );
  }

  const currentGrouped = groupItems(filteredCurrent, true);
  const availableGrouped = groupItems(filteredAvailable, false);

  return (
    <div className="gm-panel-overlay">
      <div className="gm-panel-backdrop" onClick={() => !loading && onClose()} />
      <div className="gm-panel animate-slide-in-right" style={{ maxWidth: '36rem' }}>
        {/* Header */}
        <div className="gm-panel-header">
          <div>
            <h2 className="gm-panel-title">
              <BarChart3 size={20} color="var(--admin-primary)" /> Instruments
            </h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--admin-text-secondary)', marginTop: 2 }}>
              Group: <span style={{ fontWeight: 500, color: 'var(--admin-text-primary)' }}>{groupName}</span>
            </p>
          </div>
          <button onClick={onClose} disabled={loading} className="gm-panel-close">
            <X size={20} />
          </button>
        </div>

        {/* Stats */}
        <div className="gm-panel-stats">
          <div className="gm-panel-stat">
            <p className="gm-panel-stat-value">{currentInstruments.length}</p>
            <p className="gm-panel-stat-label">Assigned</p>
          </div>
          <div className="gm-panel-stat">
            <p className="gm-panel-stat-value">{availableInstruments.length}</p>
            <p className="gm-panel-stat-label">Available</p>
          </div>
          <div className="gm-panel-stat">
            <p className="gm-panel-stat-value gm-panel-stat-value-teal">{selected.size}</p>
            <p className="gm-panel-stat-label">Selected</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="gm-tabs">
          <button onClick={() => { setActiveTab('current'); setSelected(new Set()); }}
            className={`gm-tab-btn ${activeTab === 'current' ? 'gm-tab-btn-active' : ''}`}>
            Current ({currentInstruments.length})
          </button>
          <button onClick={() => { setActiveTab('available'); setSelected(new Set()); }}
            className={`gm-tab-btn ${activeTab === 'available' ? 'gm-tab-btn-active' : ''}`}>
            Available ({availableInstruments.length})
          </button>
        </div>

        {/* Search */}
        <div className="gm-panel-search">
          <div className="gm-search-input-wrap" style={{ maxWidth: '100%' }}>
            <Search className="gm-search-icon" />
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search instruments..."
              className="gm-search-input" />
            {searchTerm && <button onClick={() => setSearchTerm('')} className="gm-search-clear"><X size={14} /></button>}
          </div>
        </div>

        {/* Bulk bar */}
        {selected.size > 0 && (
          <div className="gm-bulk-bar">
            <span className="gm-bulk-bar-text">{selected.size} selected</span>
            <div className="gm-bulk-bar-actions">
              {activeTab === 'current' ? (
                <button onClick={() => bulkAction('remove')} disabled={loading} className="gm-btn-xs gm-btn-xs-danger">
                  <Trash2 size={12} /> Remove
                </button>
              ) : (
                <button onClick={() => bulkAction('add')} disabled={loading} className="gm-btn-xs gm-btn-xs-teal">
                  <Plus size={12} /> Add
                </button>
              )}
              <button onClick={() => setSelected(new Set())} className="gm-btn-xs-clear">Clear</button>
            </div>
          </div>
        )}

        {/* Messages */}
        {error && <div className="gm-panel-msg gm-panel-msg-error"><AlertCircle size={16} style={{ flexShrink: 0 }} /> {error}</div>}
        {successMsg && <div className="gm-panel-msg gm-panel-msg-success"><CheckCircle2 size={16} style={{ flexShrink: 0 }} /> {successMsg}</div>}

        {/* Content */}
        <div className="gm-list">
          {activeTab === 'current' && (
            Object.keys(currentGrouped).length > 0 ? (
              Object.entries(currentGrouped).map(([cat, items]) => (
                <div key={cat} className="gm-cat-group">
                  <h4 className="gm-cat-heading">
                    <span>{CAT_ICONS[cat] || '\uD83D\uDCCC'}</span> {cat}
                    <span className="gm-cat-heading-count">({items.length})</span>
                  </h4>
                  {/* Column headers */}
                  <div style={{ display: 'flex', alignItems: 'center', padding: '4px 12px', fontSize: '0.7rem', fontWeight: 600, color: 'var(--admin-text-light)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #f0f0f1', marginBottom: 4 }}>
                    <span style={{ width: 20, flexShrink: 0 }} />
                    <span style={{ flex: 2, minWidth: 80 }}>Symbol</span>
                    <span style={{ flex: 1, minWidth: 60 }}>Type</span>
                    <span style={{ flex: 1, minWidth: 70 }}>Spread</span>
                    <span style={{ flex: 1, minWidth: 70 }}>Swap</span>
                    <span style={{ width: 28, flexShrink: 0 }} />
                  </div>
                  {items.map((inst) => {
                    const isEditing = editingSpreads[inst.symbol] !== undefined;
                    const isEditingSwap = editingSwaps[inst.symbol] !== undefined;
                    const currentSpread = inst.spread_pipettes || '';
                    const currentSwap = inst.swap !== null && inst.swap !== undefined ? inst.swap : null;
                    return (
                      <div key={inst.symbol}
                        className={`gm-list-item ${selected.has(inst.symbol) ? 'gm-list-item-selected' : ''}`}
                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="checkbox" checked={selected.has(inst.symbol)} onChange={() => toggleSelect(inst.symbol)} className="gm-list-checkbox" style={{ flexShrink: 0, width: 18 }} />
                        <span className="gm-list-item-symbol" style={{ flex: 2, minWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inst.symbol}</span>
                        <span className="gm-list-item-type" style={{ flex: 1, minWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inst.type || 'Other'}</span>
                        {isEditing ? (
                          <div className="gm-spread-edit-row" style={{ flex: 1, minWidth: 70 }}>
                            <input
                              type="text" defaultValue={currentSpread} placeholder="pipettes"
                              className="gm-spread-edit-input" style={{ width: '100%', minWidth: 0 }}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveSpread(inst.symbol, e.target.value);
                                if (e.key === 'Escape') setEditingSpreads((p) => { const n = { ...p }; delete n[inst.symbol]; return n; });
                              }}
                              id={`sp-${inst.symbol}`}
                            />
                            <button onClick={() => saveSpread(inst.symbol, document.getElementById(`sp-${inst.symbol}`)?.value || '')}
                              className="gm-spread-btn-ok"><Check size={12} /></button>
                            <button onClick={() => setEditingSpreads((p) => { const n = { ...p }; delete n[inst.symbol]; return n; })}
                              className="gm-spread-btn-cancel"><X size={12} /></button>
                          </div>
                        ) : (
                          <span onClick={() => setEditingSpreads((p) => ({ ...p, [inst.symbol]: currentSpread }))}
                            className="gm-list-item-spread" title="Click to edit spread"
                            style={{ flex: 1, minWidth: 70, whiteSpace: 'nowrap' }}>
                            {currentSpread !== '' ? currentSpread : <span style={{ fontStyle: 'italic', color: 'var(--admin-text-light)' }}>default</span>}
                          </span>
                        )}
                        {/* Per-instrument swap */}
                        {isEditingSwap ? (
                          <div className="gm-spread-edit-row" style={{ flex: 1, minWidth: 70 }}>
                            <input
                              type="text" defaultValue={currentSwap !== null ? currentSwap : ''} placeholder="group default"
                              className="gm-spread-edit-input" style={{ width: '100%', minWidth: 0 }}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveSwap(inst.symbol, e.target.value);
                                if (e.key === 'Escape') setEditingSwaps((p) => { const n = { ...p }; delete n[inst.symbol]; return n; });
                              }}
                              id={`sw-${inst.symbol}`}
                            />
                            <button onClick={() => saveSwap(inst.symbol, document.getElementById(`sw-${inst.symbol}`)?.value || '')}
                              className="gm-spread-btn-ok"><Check size={12} /></button>
                            <button onClick={() => setEditingSwaps((p) => { const n = { ...p }; delete n[inst.symbol]; return n; })}
                              className="gm-spread-btn-cancel"><X size={12} /></button>
                          </div>
                        ) : (
                          <span onClick={() => setEditingSwaps((p) => ({ ...p, [inst.symbol]: currentSwap !== null ? currentSwap : '' }))}
                            className="gm-list-item-spread" title="Click to edit per-instrument swap rate"
                            style={currentSwap !== null ? { flex: 1, minWidth: 70, whiteSpace: 'nowrap', color: 'var(--admin-primary)', fontWeight: 500 } : { flex: 1, minWidth: 70, whiteSpace: 'nowrap' }}>
                            {currentSwap !== null ? `${(currentSwap * 100).toFixed(3)}%` : <span style={{ fontStyle: 'italic', color: 'var(--admin-text-light)' }}>group</span>}
                          </span>
                        )}
                        <button onClick={() => removeInstrument(inst.symbol)} disabled={loading}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 28, height: 28, flexShrink: 0,
                            background: 'none', border: 'none',
                            borderRadius: 6, cursor: 'pointer', color: 'var(--admin-text-light)',
                          }}
                          onMouseOver={(e) => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#ef4444'; }}
                          onMouseOut={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--admin-text-light)'; }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))
            ) : (
              <div className="gm-list-empty">
                <BarChart3 className="gm-list-empty-icon" />
                <p className="gm-list-empty-text">{searchTerm ? 'No instruments match your search.' : 'No instruments assigned.'}</p>
              </div>
            )
          )}

          {activeTab === 'available' && (
            Object.keys(availableGrouped).length > 0 ? (
              Object.entries(availableGrouped).map(([cat, items]) => (
                <div key={cat} className="gm-cat-group">
                  <h4 className="gm-cat-heading">
                    <span>{CAT_ICONS[cat] || '\uD83D\uDCCC'}</span> {cat}
                    <span className="gm-cat-heading-count">({items.length})</span>
                  </h4>
                  {items.map((sym) => (
                    <div key={sym}
                      className={`gm-list-item ${selected.has(sym) ? 'gm-list-item-selected' : ''}`}>
                      <input type="checkbox" checked={selected.has(sym)} onChange={() => toggleSelect(sym)} className="gm-list-checkbox" />
                      <span className="gm-list-item-symbol">{sym}</span>
                      <span className="gm-list-item-type">{availableMap[sym]?.type || 'Other'}</span>
                      <button onClick={() => addInstrument(sym)} disabled={loading}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          width: 28, height: 28, background: 'none', border: 'none',
                          borderRadius: 6, cursor: 'pointer', color: 'var(--admin-text-light)',
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(0,184,148,0.08)'; e.currentTarget.style.color = 'var(--admin-primary)'; }}
                        onMouseOut={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--admin-text-light)'; }}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div className="gm-list-empty">
                <BarChart3 className="gm-list-empty-icon" />
                <p className="gm-list-empty-text">{searchTerm ? 'No instruments match your search.' : 'All instruments are already assigned.'}</p>
              </div>
            )
          )}

          {/* Manual add section */}
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #f0f0f1' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--admin-text-primary)', marginBottom: 12 }}>
              Add Manually
            </h4>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--admin-text-secondary)', marginBottom: 4 }}>Symbol</label>
                <input type="text" value={newSymbol} onChange={(e) => setNewSymbol(e.target.value)} placeholder="e.g. EURUSD"
                  className="gm-input" style={{ width: 144 }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--admin-text-secondary)', marginBottom: 4 }}>Spread (pipettes)</label>
                <input type="text" value={newSpread} onChange={(e) => setNewSpread(e.target.value)} placeholder="default"
                  className="gm-input" style={{ width: 128 }} />
              </div>
              <button onClick={() => { if (newSymbol.trim()) { addInstrument(newSymbol.trim(), newSpread); setNewSymbol(''); setNewSpread(''); } }}
                disabled={loading || !newSymbol.trim()}
                className="gm-btn-primary">
                <Plus size={16} /> Add
              </button>
            </div>
            <p style={{ marginTop: 8, fontSize: '0.75rem', color: 'var(--admin-text-light)' }}>
              Leave spread empty to use the instrument default.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
